<script setup lang="ts">
import type { SearchResultAssetFragment } from '#graphql-operations'
import type { CarouselApi } from '@layers/ui/app/components/ui/carousel'

const props = defineProps<{
  images: (SearchResultAssetFragment & { alt: string })[]
  imageIndex: number
}>()

const emit = defineEmits<{
  (e: 'update:image-index', index: number): void
}>()

const emblaMainApi = ref<CarouselApi>()
const emblaThumbnailApi = ref<CarouselApi>()
const selectedIndex = ref(0)

function onSelect() {
  if (!emblaMainApi.value || !emblaThumbnailApi.value)
    return
  selectedIndex.value = emblaMainApi.value.selectedScrollSnap()
  emblaThumbnailApi.value.scrollTo(emblaMainApi.value.selectedScrollSnap())
}

function scrollTo(index: number, jump = false) {
  if (!emblaMainApi.value || !emblaThumbnailApi.value)
    return
  emblaMainApi.value.scrollTo(index, jump)
}

watchOnce(emblaMainApi, (emblaMainApi) => {
  if (!emblaMainApi)
    return

  onSelect()
  emblaMainApi.on('select', onSelect)
  emblaMainApi.on('reInit', onSelect)
})

watch(() => props.imageIndex, imageIndex => scrollTo(imageIndex, true))

watch(selectedIndex, (newIndex) => {
  emit('update:image-index', newIndex)
}, { immediate: true })
</script>

<template>
  <div class="wfull sm:w-auto">
    <UiCarousel
      class="relative mb0 wfull rd-md md:mb4"
      :opts="{ duration: 20 }"
      @init-api="(val) => emblaMainApi = val"
    >
      <UiCarouselContent>
        <UiCarouselItem v-for="(image, index) in images" :key="index">
          <NuxtImg
            :src="image.preview"
            :alt="image.alt"
            width="640"
            height="640"
            class="aspect-square hfull wfull select-none object-cover object-center lg:rd-md"
            preload
          />
        </UiCarouselItem>
      </UiCarouselContent>
      <UiCarouselPrevious class="absolute left-4 top-1/2 z10 h-12 w-12 bg-background">
        <Icon name="lucide:chevron-left" class="text-xl" />
      </UiCarouselPrevious>
      <UiCarouselNext class="absolute right-4 top-1/2 z10 h-12 w-12 bg-background">
        <Icon name="lucide:chevron-right" class="text-xl" />
      </UiCarouselNext>

      <slot />
    </UiCarousel>

    <!-- Bullets for mobile devices -->
    <div class="mt-4 flex justify-center space-x-2 sm:hidden">
      <button
        v-for="(_, index) in images"
        :key="index"
        class="h-2.5 w-2.5 rounded-full transition-colors duration-200"
        :class="index === selectedIndex ? 'bg-orange' : 'bg-gray-300'"
        :aria-label="`Go to slide ${index + 1}`"
        @click="scrollTo(index)"
      />
    </div>

    <!-- Thumbnails for larger devices -->
    <UiCarousel
      class="relative wfull bg-gray-100 p2 hidden sm:block md:bg-white md:p0"
      :opts="{
        containScroll: 'keepSnaps',
        dragFree: true,
      }"
      @init-api="(val) => emblaThumbnailApi = val"
    >
      <UiCarouselContent class="ml0 flex gap-2">
        <UiCarouselItem v-for="(image, index) in images" :key="index" class="basis-[calc((100%-(5*0.5rem))/6)] cursor-pointer pl0" @click="scrollTo(index)">
          <div
            class="border-2 n-border-base rd-md"
            :class="index === selectedIndex ? 'border-orange' : 'op50'"
          >
            <NuxtImg
              :src="image.preview"
              :alt="image.alt"
              width="100"
              height="100"
              placeholder
              class="aspect-square hfull wfull select-none select-none rd-md object-cover object-center"
              loading="lazy"
            />
          </div>
        </UiCarouselItem>
      </UiCarouselContent>
    </UiCarousel>
  </div>
</template>
